import { LoyaltyProgram, Transaction, RedemptionOption, AIRecommendation, UserPreferences, ConversionRate, ProgramMetrics, CalculationResult } from '../types';

/**
 * Core optimization engine for loyalty program calculations and recommendations
 */
export class LoyaltyOptimizationEngine {
  
  /**
   * Calculate the real value of a redemption option
   */
  calculateRedemptionValue(program: LoyaltyProgram, redemption: RedemptionOption): CalculationResult {
    const baseValue = redemption.value / redemption.pointsRequired; // cents per point
    const programMultiplier = program.conversionRate || 1;
    
    // Factors that affect value
    const factors: string[] = [];
    let adjustedValue = baseValue * programMultiplier;
    let confidence = 0.8; // base confidence
    
    // Availability affects value
    if (redemption.availability === 'low') {
      adjustedValue *= 1.15; // premium for scarce options
      factors.push('Limited availability (+15%)');
      confidence -= 0.1;
    } else if (redemption.availability === 'high') {
      confidence += 0.1;
      factors.push('High availability');
    }
    
    // Category-based adjustments
    if (redemption.category === 'flight') {
      adjustedValue *= 1.1; // flights typically offer better value
      factors.push('Flight redemption bonus (+10%)');
    } else if (redemption.category === 'cash') {
      adjustedValue *= 0.9; // cash redemptions typically lower value
      factors.push('Cash redemption penalty (-10%)');
    }
    
    // Trending items might be overvalued
    if (redemption.trending) {
      confidence -= 0.05;
      factors.push('Trending item (lower confidence)');
    }
    
    return {
      value: Math.round(adjustedValue * 100) / 100, // round to cents
      confidence: Math.max(0.1, Math.min(0.95, confidence)),
      factors,
      lastCalculated: new Date()
    };
  }
  
  /**
   * Find the best transfer options for points
   */
  findBestTransfers(fromProgram: LoyaltyProgram, targetPrograms: LoyaltyProgram[], points: number): ConversionRate[] {
    const transfers: ConversionRate[] = [];
    
    targetPrograms.forEach(targetProgram => {
      if (targetProgram.id === fromProgram.id) return;
      
      // Calculate transfer rate based on program types and relationships
      let baseRate = this.calculateTransferRate(fromProgram.type, targetProgram.type);
      let fees = this.calculateTransferFees(points, fromProgram.type, targetProgram.type);
      
      // Common transfer partnerships
      const transferRate: ConversionRate = {
        fromProgram: fromProgram.id,
        toProgram: targetProgram.id,
        rate: baseRate,
        fees,
        minTransfer: 1000,
        maxTransfer: 500000,
        lastUpdated: new Date()
      };
      
      transfers.push(transferRate);
    });
    
    // Sort by effective rate (accounting for fees)
    return transfers.sort((a, b) => {
      const aEffectiveRate = a.rate - (a.fees || 0) / points;
      const bEffectiveRate = b.rate - (b.fees || 0) / points;
      return bEffectiveRate - aEffectiveRate;
    });
  }
  
  /**
   * Predict when points will expire based on earning patterns
   */
  predictExpiry(program: LoyaltyProgram, transactions: Transaction[]): Date | null {
    if (!program.expiryDate) return null;
    
    // If expiry date is set, check earning patterns
    const recentEarning = transactions
      .filter(t => t.type === 'earned' && t.date > new Date(Date.now() - 90 * 24 * 60 * 60 * 1000))
      .reduce((sum, t) => sum + t.points, 0);
    
    // If actively earning, points likely won't expire
    if (recentEarning > 0) {
      return null; // Active account, expiry unlikely
    }
    
    return program.expiryDate;
  }
  
  /**
   * Generate AI-powered recommendations
   */
  generateRecommendations(programs: LoyaltyProgram[], preferences: UserPreferences, transactions: Transaction[]): AIRecommendation[] {
    const recommendations: AIRecommendation[] = [];
    const now = new Date();
    
    programs.forEach(program => {
      const programTransactions = transactions.filter(t => t.programId === program.id);
      
      // Check for expiring points
      const expiryDate = this.predictExpiry(program, programTransactions);
      if (expiryDate) {
        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 3600 * 24));
        
        if (daysUntilExpiry <= 60 && daysUntilExpiry > 0) {
          recommendations.push({
            id: `expiry-${program.id}`,
            type: 'urgent',
            title: `${program.points.toLocaleString()} ${program.currency} expiring soon`,
            description: `Your ${program.name} points expire in ${daysUntilExpiry} days. Consider redeeming or transferring them.`,
            action: 'Redeem or transfer points',
            programsInvolved: [program.id],
            pointsInvolved: program.points,
            potentialValue: program.points * (program.conversionRate || 1) * 0.01,
            confidence: 0.9,
            priority: daysUntilExpiry <= 30 ? 'high' : 'medium',
            expiryDate
          });
        }
      }
      
      // Recommend transfers for low-value programs
      if (program.points > 10000 && (program.conversionRate || 1) < 1.0) {
        recommendations.push({
          id: `transfer-${program.id}`,
          type: 'transfer',
          title: `Transfer from ${program.name} for better value`,
          description: `Transfer your ${program.currency} to a higher-value program for better redemptions.`,
          action: 'Review transfer options',
          programsInvolved: [program.id],
          pointsInvolved: program.points,
          potentialValue: program.points * 0.005, // estimated 0.5cpp improvement
          confidence: 0.7,
          priority: 'medium'
        });
      }
      
      // Recommend redemptions for high-balance accounts
      if (program.points > 100000) {
        recommendations.push({
          id: `redeem-${program.id}`,
          type: 'redeem',
          title: `Consider redeeming ${program.name} points`,
          description: `You have a substantial balance. Consider high-value redemptions to maximize your rewards.`,
          action: 'Browse redemption options',
          programsInvolved: [program.id],
          pointsInvolved: Math.min(program.points, 50000),
          potentialValue: 50000 * (program.conversionRate || 1) * 0.015, // 1.5cpp target
          confidence: 0.6,
          priority: 'low'
        });
      }
    });
    
    // Sort by priority and confidence
    return recommendations
      .sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        const aPriority = priorityOrder[a.priority];
        const bPriority = priorityOrder[b.priority];
        
        if (aPriority !== bPriority) return bPriority - aPriority;
        return b.confidence - a.confidence;
      })
      .slice(0, 5); // Top 5 recommendations
  }
  
  /**
   * Calculate program metrics for analytics
   */
  calculateProgramMetrics(program: LoyaltyProgram, transactions: Transaction[]): ProgramMetrics {
    const programTransactions = transactions.filter(t => t.programId === program.id);
    
    const totalEarned = programTransactions
      .filter(t => t.type === 'earned')
      .reduce((sum, t) => sum + t.points, 0);
    
    const totalRedeemed = programTransactions
      .filter(t => t.type === 'redeemed')
      .reduce((sum, t) => sum + Math.abs(t.points), 0);
    
    // Calculate earning rate (points per month)
    const firstTransaction = programTransactions.sort((a, b) => a.date.getTime() - b.date.getTime())[0];
    const monthsSinceFirstTransaction = firstTransaction 
      ? Math.max(1, (new Date().getTime() - firstTransaction.date.getTime()) / (1000 * 3600 * 24 * 30))
      : 1;
    
    const averageEarnRate = totalEarned / monthsSinceFirstTransaction;
    
    // Calculate average redemption value
    const redemptions = programTransactions.filter(t => t.type === 'redeemed');
    const averageRedemptionValue = redemptions.length > 0
      ? (program.conversionRate || 1) * 1.2 // Estimated based on program conversion rate
      : 0;
    
    const lastActivity = programTransactions.length > 0
      ? new Date(Math.max(...programTransactions.map(t => t.date.getTime())))
      : new Date();
    
    return {
      programId: program.id,
      totalEarned,
      totalRedeemed,
      currentBalance: program.points,
      averageEarnRate,
      averageRedemptionValue,
      lastActivity,
      projectedExpiry: this.predictExpiry(program, transactions)
    };
  }
  
  /**
   * Private helper methods
   */
  private calculateTransferRate(fromType: string, toType: string): number {
    // Standard transfer rates between program types
    const transferMatrix: Record<string, Record<string, number>> = {
      'credit_card': { 'airline': 1.0, 'hotel': 1.0, 'credit_card': 1.0 },
      'airline': { 'airline': 1.0, 'hotel': 0.8, 'credit_card': 0.7 },
      'hotel': { 'airline': 1.25, 'hotel': 1.0, 'credit_card': 0.7 }
    };
    
    return transferMatrix[fromType]?.[toType] || 0.5;
  }
  
  private calculateTransferFees(points: number, fromType: string, toType: string): number {
    // Base fee structure for transfers
    if (fromType === 'credit_card') return 0; // Credit card transfers usually free
    if (points < 5000) return 25; // Small transfer fee
    if (points > 100000) return 50; // Large transfer fee
    return 30; // Standard fee
  }
}

// Export singleton instance
export const optimizationEngine = new LoyaltyOptimizationEngine();